import React, { useState } from 'react';
import { Upload, FileText, X, CheckCircle, Sparkles } from 'lucide-react';
import { useMyContext } from '../../../context/MyContext';
import * as Papa from 'papaparse';
import { Base_Url, uploadPincode } from '../../../config/config';
import toast from 'react-hot-toast';
import { FiEye } from 'react-icons/fi';
import { FiDownload } from 'react-icons/fi';
import NavBar from '../../../component/b2c/navBar/navBar';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import FullPageLoader from '../../../component/fullPageLoader/fullPageLoader';

export default function UploadPincodeComponent() {
  const [file, setFile] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPartner, setSelectedPartner] = useState('bluedart');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const token = localStorage.getItem('token');
  const cus_id = localStorage.getItem('cus_id');

  const partners = [
    { value: 'bluedart', label: 'BlueDart', icon: '🚚' },
    { value: 'delhivery', label: 'Delhivery', icon: '🚚' },
    { value: 'ecom', label: 'Ecom Express', icon: '🚚' },
    { value: 'ekart', label: 'Ekart', icon: '🚚' },
    { value: 'xpressbees', label: 'XpressBees', icon: '🚚' }
  ];

  const API_CONFIG = {
    endpoint: Base_Url + uploadPincode,
    cus_id: cus_id,
    token: token
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError('');
    setSuccess('');

    if (selectedFile && selectedFile.type === 'text/csv') {
      Papa.parse(selectedFile, {
        complete: (results) => {
          if (results.data && results.data.length > 0) {
            const headers = results.data[0];
            const dataRows = results.data.slice(1).filter(row => row.some(cell => cell.trim() !== ''));

            const parsedData = dataRows.map(row => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header] = row[index] || '';
              });
              return obj;
            });

            setPreviewData(parsedData);
          }
        },
        header: false,
        skipEmptyLines: true,
        error: (error) => {
          setError('Error parsing CSV file: ' + error.message);
        }
      });
    }
  };

  const openPreviewModal = () => {
    if (previewData && previewData.length > 0) {
      setShowPreview(true);
    } else {
      setError('No valid data to preview');
    }
  };

  const closePreviewModal = () => {
    setShowPreview(false);
  };

  const handlePartnerSelect = (partner) => {
    setSelectedPartner(partner.value);
    setIsDropdownOpen(false);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      toast.error('Please select a file first');
      return;
    }
    if (!previewData || previewData.length === 0) {
      setError('No valid data to upload');
      toast.error('No valid data to upload');
      return;
    }
    setIsUploading(true);
    setError('');
    setSuccess('');
    setIsPageLoading(true);
    try {
      const formData = new FormData();
      formData.append('csv_file', file);
      formData.append('partner', selectedPartner);

      const response = await fetch(API_CONFIG.endpoint, {
        method: 'POST',
        headers: {
          'cus_id': API_CONFIG.cus_id,
          'token': API_CONFIG.token,
        },
        body: formData,
      });
      const result = await response.json();

      if (response.ok) {
        setSuccess('File uploaded successfully!');
        toast.success('File uploaded successfully!');
        setFile(null);
        setPreviewData(null);
        setShowPreview(false);
        document.querySelector('input[type="file"]').value = '';
        setIsPageLoading(false);
      } else {
        const message = result?.messages?.csv_file || result?.message || 'Upload failed';
        setError(message);
        toast.error(message);
        setIsPageLoading(false);
      }
    } catch (error) {
      const message = error?.message || 'Upload failed';
      setError('Upload failed: ' + message);
      toast.error('Upload failed: ' + message);
      setIsPageLoading(false);
    } finally {
      setIsUploading(false);
      setIsPageLoading(false);
    }
  };

  // Custom Dropdown Component
  const PartnerDropdown = () => {
    const selectedPartnerObj = partners.find(p => p.value === selectedPartner);

    return (
      <div className="relative group">
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 flex items-center">
          <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mr-2 animate-pulse"></div>
          Select Delivery Partner
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="relative w-full backdrop-blur-xl bg-white/30 dark:bg-darkmodeColor border-2 border-transparent rounded-2xl shadow-lg pl-4 pr-10 py-3 text-left cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl focus:outline-none focus:border-purple-500"
          >
            <span className="flex items-center">
              <span className="text-lg mr-3">{selectedPartnerObj?.icon}</span>
              <span className="block truncate text-gray-900 dark:text-white font-semibold text-xs">
                {selectedPartnerObj?.label}
              </span>
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </span>
          </button>

          {isDropdownOpen && (
            <div className="absolute z-50 mt-2 w-full backdrop-blur-xl bg-white/90 dark:bg-darkmodeColor shadow-2xl rounded-2xl border border-white/20 dark:border-white/10 animate-in fade-in-0 zoom-in-95 duration-200">
              <div className="py-2">
                {partners.map((partner) => (
                  <button
                    key={partner.value}
                    type="button"
                    onClick={() => handlePartnerSelect(partner)}
                    className={`w-full text-left px-4 py-3 text-xs transition-all duration-200 flex items-center hover:bg-purple-50 dark:hover:bg-purple-900/20 ${selectedPartner === partner.value
                      ? 'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 text-purple-700 dark:text-purple-300'
                      : 'text-gray-900 dark:text-gray-300'
                      }`}
                  >
                    <span className="text-lg mr-3">{partner.icon}</span>
                    <span className="font-semibold">{partner.label}</span>
                    {selectedPartner === partner.value && (
                      <span className="ml-auto">
                        <CheckCircle className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Preview Modal Component
  const PreviewModal = () => {
    if (!previewData || previewData.length === 0) return null;

    const headers = Object.keys(previewData[0]);
    const selectedPartnerObj = partners.find(p => p.value === selectedPartner);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="backdrop-blur-xl bg-white/95 dark:bg-slate-900/95 rounded-3xl shadow-2xl w-11/12 max-w-6xl max-h-[90vh] overflow-hidden border border-white/20 dark:border-white/10 transform animate-in fade-in-0 zoom-in-95 duration-300">

          {/* Enhanced Header with Gradient */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-customPurple via-customPurple to-customPurple relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/80 via-pink-600/80 to-blue-600/80" style={{
              animation: 'gradient-x 15s ease infinite'
            }}></div>

            <div className="flex items-center space-x-3 relative z-10">
              <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm animate-pulse">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  Pincode Data Preview
                </h2>
                <p className="text-purple-100 text-sm flex items-center">
                  Review your uploaded data for
                  <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs flex items-center">
                    {selectedPartnerObj?.icon} {selectedPartnerObj?.label}
                  </span>
                </p>
              </div>
              <Sparkles className="w-5 h-5 text-yellow-300 animate-pulse" />
            </div>

            <button
              onClick={closePreviewModal}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-all duration-200 text-white hover:scale-110 transform relative z-10"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content Area */}
          <div className="overflow-auto max-h-[70vh] p-4 bg-gradient-to-br from-gray-50/50 to-white/50 dark:from-slate-800/50 dark:to-slate-900/50">

            {/* Data Stats Card */}
            <div className="mb-4 backdrop-blur-xl bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-4 border border-blue-200/50 dark:border-blue-800/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">Data Summary</h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Total Records: <span className="font-bold text-blue-600 dark:text-blue-400">{previewData.length}</span> |
                      Columns: <span className="font-bold text-blue-600 dark:text-blue-400">{headers.length}</span> |
                      Partner: <span className="font-bold text-purple-600 dark:text-purple-400">{selectedPartnerObj?.label}</span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Showing first 50 rows</p>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="rounded-2xl overflow-hidden border border-gray-200/50 dark:border-gray-700/50 shadow-lg backdrop-blur-sm bg-white/80 dark:bg-slate-800/80">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-gradient-to-r from-indigo-500 to-purple-600 backdrop-blur-sm">
                    <tr>
                      {headers.map((header, index) => (
                        <th
                          key={index}
                          className="p-4 text-left font-semibold text-white text-sm uppercase tracking-wide border-r border-white/20 last:border-r-0"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 50).map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={`${rowIndex % 2 === 0
                          ? 'bg-gray-50/50 dark:bg-gray-800/30'
                          : 'bg-white/50 dark:bg-gray-700/30'
                          } hover:bg-blue-50/70 dark:hover:bg-blue-900/20 transition-all duration-200`}
                      >
                        {headers.map((header, colIndex) => (
                          <td
                            key={colIndex}
                            className="p-4 text-sm text-gray-700 dark:text-gray-300 border-r border-gray-200/50 dark:border-gray-600/50 last:border-r-0"
                          >
                            <div className="truncate max-w-xs" title={row[header]}>
                              {row[header]}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer Info */}
            {previewData.length > 50 && (
              <div className="text-center mt-4 p-3 backdrop-blur-xl bg-gradient-to-r from-amber-50/80 to-orange-50/80 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl border border-amber-200/50 dark:border-amber-800/50">
                <span className="text-sm text-amber-700 dark:text-amber-300 font-medium">
                  Showing first 50 rows of {previewData.length} total rows
                </span>
              </div>
            )}
          </div>

          {/* Action Footer */}
          <div className="p-4 backdrop-blur-xl bg-gradient-to-r from-gray-50/80 to-white/80 dark:from-slate-800/80 dark:to-slate-900/80 border-t border-gray-200/50 dark:border-gray-700/50">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Data loaded successfully for {selectedPartnerObj?.label}</span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={closePreviewModal}
                  className="px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-xl font-medium text-sm transition-all duration-200 transform hover:scale-105"
                >
                  Close Preview
                </button>
                <button
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-medium text-sm transition-all duration-200 transform hover:scale-105 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="w-4 h-4" />
                  <span>{isUploading ? 'Uploading...' : 'Proceed with Upload'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (


    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
      {showPreview && <PreviewModal />}

      <FullPageLoader
        isLoading={isPageLoading}
        message="Processing your request..."
      />

      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1000ms' }}></div>
        <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-pink-500/10 rounded-full blur-2xl animate-bounce" style={{ animationDelay: '500ms' }}></div>
      </div>

      <div className="z-10">
        <NavBar />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className={`fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20`}
            style={
              window.innerWidth < 768
                ? { top: '113px', height: 'calc(100vh - 115px)', overflowY: 'auto' }
                : {}
            }
          >
            <Sidebar />
          </div>
        )}
        <div className=' custom-scrollbar bg-gray-100 overflow-y-auto dark:bg-darkmodeColor border border-white/20 dark:border-white/10  w-full mb-3   shadow-2xl'>
          <div className={`p-2 md:p-3 min-h-[30vh] overflow-y-auto ${isToggleSidebar ? 'ml-0' : 'mr-0'} ${isToggleSidebar ? 'w-full' : 'w-full'} mb-4 md:mb-0`}>
            <div className="max-w-4xl mx-auto">
              <div className="backdrop-blur-xl bg-white/10 dark:bg-darkmodeColor border border-white/20 dark:border-white/10 rounded-3xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-all duration-500">

                {/* Header with Gradient and Animation */}
                <div className="bg-gradient-to-r from-customPurple via-customPurple to-customPurple p-1.5 text-white rounded-t-3xl relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/80 via-pink-600/80 to-blue-600/80" style={{
                    animation: 'gradient-x 15s ease infinite'
                  }}></div>
                  <div className="relative z-10">
                    <div className="flex items-center space-x-2 mb-1">
                      <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm animate-pulse">
                        <Upload className="w-3 h-3" />
                      </div>
                      <h2 className="text-xs font-bold bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent">
                        Upload Pincode List
                      </h2>
                      <Sparkles className="w-4 h-4 text-yellow-300 animate-pulse" />
                    </div>
                    <p className="text-purple-100 text-xs ml-5">Transform your pincode data with intelligent CSV processing</p>
                  </div>

                  {/* Floating Elements */}
                  <div className="absolute top-2 right-2" style={{ animation: 'float 3s ease-in-out infinite' }}>
                    <div className="w-2 h-2 bg-white/30 rounded-full"></div>
                  </div>
                  <div className="absolute top-6 right-6" style={{ animation: 'float 3s ease-in-out infinite', animationDelay: '300ms' }}>
                    <div className="w-1.5 h-1.5 bg-white/40 rounded-full"></div>
                  </div>
                  <div className="absolute top-4 right-10" style={{ animation: 'float 3s ease-in-out infinite', animationDelay: '700ms' }}>
                    <div className="w-2.5 h-2.5 bg-white/20 rounded-full"></div>
                  </div>
                </div>

                {/* Success/Error Messages */}
                {error && (
                  <div className="mx-4 mt-3 p-3 backdrop-blur-xl bg-red-50/80 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-center">
                      <X className="w-4 h-4 text-red-500 mr-2" />
                      <p className="text-red-600 dark:text-red-400 text-xs font-medium">{error}</p>
                    </div>
                  </div>
                )}

                {success && (
                  <div className="mx-4 mt-3 p-3 backdrop-blur-xl bg-green-50/80 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <p className="text-green-600 dark:text-green-400 text-xs font-medium">{success}</p>
                    </div>
                  </div>
                )}

                {/* Partner Selection with Enhanced Styling */}
                <div className='mt-3 mx-4'>
                  <PartnerDropdown />
                </div>

                {/* File Upload Section with Drag & Drop */}
                <div className="p-4">
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 flex items-center">
                      <div className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mr-2 animate-pulse"></div>
                      Upload Your CSV File
                    </label>

                    {/* Drag & Drop Zone */}
                    <div className="relative border-2 border-dashed rounded-2xl p-4 transition-all duration-300 backdrop-blur-xl bg-white/30 dark:bg-darkmodeColor hover:bg-white/40 dark:hover:bg-black/30 group cursor-pointer border-purple-300 dark:border-purple-600 hover:border-purple-500">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />

                      <div className="text-center">
                        <div className="mx-auto w-10 h-10 bg-customPurple rounded-full flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300">
                          <Upload className="w-5 h-5 text-white animate-bounce" />
                        </div>
                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                          Drop your CSV file here or click to browse
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Supports CSV files up to 10MB
                        </p>

                        {file && (
                          <div className="mt-2 p-2 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-xl border border-green-200 dark:border-green-800">
                            <div className="flex items-center justify-center space-x-2">
                              <CheckCircle className="w-4 h-4 text-green-600 animate-pulse" />
                              <span className="font-medium text-xs text-green-800 dark:text-green-200">{file.name}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col md:flex-row gap-2 mt-3">
                      <button
                        type="button"
                        onClick={handleUpload}
                        disabled={!file || isUploading}
                        className={`group flex-1 p-2.5 rounded-xl font-semibold text-xs transition-all duration-500 transform hover:scale-[1.02] shadow-lg hover:shadow-xl ${!file || isUploading
                          ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed text-gray-500'
                          : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                          }`}
                      >
                        <div className="flex items-center justify-center space-x-2">
                          {isUploading ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          ) : (
                            <Upload className="w-4 h-4 group-hover:animate-bounce" />
                          )}
                          <span>{isUploading ? 'Processing...' : 'Upload & Process'}</span>
                          {!isUploading && <Sparkles className="w-4 h-4 opacity-70 group-hover:opacity-100 transition-opacity" />}
                        </div>
                      </button>

                      {file && previewData && (
                        <button
                          type="button"
                          onClick={openPreviewModal}
                          className="group flex-1 md:flex-none md:px-4 p-2.5 rounded-xl font-semibold text-xs bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white transition-all duration-500 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                        >
                          <div className="flex items-center justify-center space-x-2">
                            <FiEye className="w-4 h-4 group-hover:animate-pulse" />
                            <span>Preview Data</span>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Enhanced Template Download */}
                  <div className="mx-4 mb-4">
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-3 border border-indigo-200 dark:border-indigo-800">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full">
                            <FileText className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-xs">Need a template?</h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400">Download our pre-formatted CSV template</p>
                          </div>
                        </div>
                        <a
                          href="/sampleFile/pincodeSample.csv"
                          download="pincodeSample.csv"
                          className="group px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl font-semibold text-xs transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center space-x-1.5"
                        >
                          <FiDownload className="w-4 h-4 group-hover:animate-bounce" />
                          <span>Download Template</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <style jsx>{`
                           @keyframes gradient-x {
                             0%, 100% {
                               transform: translateX(0%);
                             }
                             50% {
                               transform: translateX(100%);
                             }
                           }
                           
                           @keyframes float {
                             0%, 100% {
                               transform: translateY(0px);
                             }
                             50% {
                               transform: translateY(-10px);
                             }
                           }
                           
                           .border-3 {
                             border-width: 3px;
                           }
                         `}</style>
      </div>
    </div>
  )
}
